import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { downloadMedia } from '@/lib/contentStudio';
import ZStudioNav from '@/pages/dashboard/apps/zstudio/components/ZStudioNav';
import ZImagePicker from '@/pages/dashboard/apps/zstudio/components/ZImagePicker';
import ZRecentGenerations from '@/pages/dashboard/apps/zstudio/components/ZRecentGenerations';
import {
  createZProject,
  createZCreation,
  logZGeneration,
  editZImage,
  zQualityToApi,
  Z_QUALITIES,
  Z_ASPECTS,
} from '@/lib/zstudio';

// Commandes naturelles rapides (spec 44).
const QUICK_EDITS: { label: string; prompt: string }[] = [
  { label: 'Changer le fond', prompt: 'Replace the background with a clean professional studio backdrop, keep the subject exactly the same.' },
  { label: 'Lumière plus chaude', prompt: 'Add warmer, golden cinematic lighting. Keep the subject and outfit identical.' },
  { label: 'Change la pose', prompt: 'Change the subject’s pose to a natural confident standing pose. Keep the same person and outfit.' },
  { label: 'Supprimer un élément', prompt: 'Remove any distracting background objects. Keep only the main subject untouched.' },
  { label: 'Décor marocain', prompt: 'Place the subject in an elegant Moroccan riad interior with zellige tiles and warm ambiance. Keep the person and outfit identical.' },
  { label: 'Version plus luxueuse', prompt: 'Make the scene more luxurious and editorial: refined lighting, elegant setting, premium look. Preserve the subject and outfit exactly.' },
  { label: 'Conserver le vêtement exactement', prompt: 'Keep the exact same garment: identical colour, cut, pattern, texture and details. Only improve lighting and background quality.' },
];

export default function ZStudioEditPage() {
  const { user } = useAuth();
  const [source, setSource] = useState<string | null>(null);
  const [instruction, setInstruction] = useState('');
  const [quality, setQuality] = useState('standard');
  const [aspect, setAspect] = useState('1:1');
  const [variants, setVariants] = useState(1);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<string[]>([]);

  const run = async () => {
    if (!user?.id || !source) { setError('Choisissez d’abord une image.'); return; }
    if (!instruction.trim()) { setError('Décrivez la modification souhaitée.'); return; }
    setRunning(true);
    setError(null);
    setResults([]);
    try {
      const q = zQualityToApi(quality);
      const project = await createZProject({
        ownerId: user.id,
        name: `Édition — ${instruction.slice(0, 80)}`,
        kind: 'image',
        sourceType: 'edit',
        brief: instruction,
        meta: { quality, aspect },
      });
      // spec 45 : variations = plusieurs résultats issus de la même consigne.
      const urls = await editZImage({
        prompt: instruction,
        images: [source],
        aspectRatio: aspect,
        quality: q.quality,
        resolution: q.resolution,
        persist: true,
        folder: 'zstudio/edits',
      });
      if (urls.length === 0) throw new Error('Aucun résultat généré.');
      setResults(urls);
      for (let i = 0; i < urls.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await createZCreation({
          ownerId: user.id,
          projectId: project.id,
          kind: 'edit',
          mediaKind: 'image',
          url: urls[i],
          thumbnail_url: urls[i],
          title: `Édition — ${instruction.slice(0, 60)}`,
          content: { prompt: instruction, source, aspect, quality, variant: i + 1 },
          provider: 'xAI',
        });
      }
      await logZGeneration({
        ownerId: user.id,
        projectId: project.id,
        type: 'edit_image',
        prompt: instruction,
        model: 'grok-imagine-image-2.0',
        provider: 'xAI',
        output: { urls },
        status: 'done',
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'L’édition a échoué.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="mb-4">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className="ri-equalizer-2-line text-accent-500"></i>Édition IA d’image
        </h2>
        <p className="text-sm text-foreground-500 mt-1">Modifiez une image existante avec une consigne en langage naturel. L’image d’origine est toujours conservée (spec 44 & 45).</p>
      </div>
      <ZStudioNav />
      <ZRecentGenerations />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2 bg-background-50 border border-background-200/70 rounded-xl p-4 space-y-4">
          <ZImagePicker value={source} onChange={setSource} label="Image à modifier" hint="Téléversez, réutilisez une création ou un produit." />

          <div>
            <p className="text-xs font-medium text-foreground-600 mb-1.5">Commande rapide</p>
            <div className="flex flex-wrap gap-1.5">
              {QUICK_EDITS.map((q) => (
                <button key={q.label} type="button" onClick={() => setInstruction(q.prompt)} className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">
                  {q.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-foreground-600 mb-1.5">Consigne (langage naturel)</p>
            <textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={4} placeholder="Ex : rends l’éclairage plus chaud, ajoute un décor marocain, conserve exactement le vêtement." className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-300 resize-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs font-medium text-foreground-600 mb-1.5">Format</p>
              <div className="flex flex-wrap gap-1.5">{Z_ASPECTS.map((a) => (
                <button key={a.value} type="button" onClick={() => setAspect(a.value)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${aspect === a.value ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600'}`}>{a.label.split(' ')[0]}</button>
              ))}</div>
            </div>
            <div>
              <p className="text-xs font-medium text-foreground-600 mb-1.5">Qualité</p>
              <div className="flex flex-wrap gap-1.5">{Z_QUALITIES.map((qq) => (
                <button key={qq.value} type="button" onClick={() => setQuality(qq.value)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${quality === qq.value ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600'}`}>{qq.label}</button>
              ))}</div>
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-foreground-600 mb-1.5">Nombre de variations</p>
            <div className="flex flex-wrap gap-1.5">
              {[1, 2, 3].map((n) => (
                <button key={n} type="button" onClick={() => setVariants(n)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${variants === n ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600'}`}>{n} version{n > 1 ? 's' : ''}</button>
              ))}
            </div>
          </div>

          {error && <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg"><i className="ri-error-warning-line"></i>{error}</div>}

          <button
            type="button"
            onClick={run}
            disabled={running || !source}
            className="w-full flex items-center justify-center gap-1.5 px-5 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            {running ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-magic-line"></i>}
            {running ? 'Édition en cours…' : 'Éditer l’image'}
          </button>
          <p className="text-[11px] text-foreground-400">Le système privilégie la modification de l’image existante plutôt qu’une régénération complète.</p>
        </div>

        <div className="lg:col-span-3 bg-background-50 border border-background-200/70 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-foreground-900">Résultat</h3>
            {results.length > 0 && (
              <Link to="/dashboard/zstudio/creations" className="text-xs font-medium text-primary-600 hover:text-primary-700 cursor-pointer">
                <i className="ri-archive-2-line mr-1"></i>Mes créations
              </Link>
            )}
          </div>
          {running ? (
            <div className="flex flex-col items-center justify-center py-24">
              <i className="ri-loader-4-line animate-spin text-3xl text-accent-500"></i>
              <p className="text-sm text-foreground-600 mt-3">Application de votre consigne…</p>
            </div>
          ) : results.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center px-6 bg-background-100 rounded-lg">
              <span className="w-14 h-14 rounded-full bg-background-50 flex items-center justify-center mb-3">
                <i className="ri-equalizer-2-line text-2xl text-foreground-400"></i>
              </span>
              <p className="text-sm text-foreground-500">Les versions éditées apparaîtront ici.</p>
            </div>
          ) : (
            <div className={`grid gap-3 ${results.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {results.map((url) => (
                <div key={url} className="rounded-xl overflow-hidden border border-background-200/70">
                  <div className="aspect-square bg-background-100">
                    <img src={url} alt="Édition" className="w-full h-full object-contain" />
                  </div>
                  <div className="flex items-center gap-2 p-2">
                    <a href={url} target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-primary-600 hover:bg-background-100 transition-colors cursor-pointer" title="Ouvrir"><i className="ri-external-link-line"></i></a>
                    <button type="button" onClick={() => downloadMedia(url, 'zstudio-edit.png')} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-accent-600 hover:bg-background-100 transition-colors cursor-pointer" title="Télécharger"><i className="ri-download-2-line"></i></button>
                    <button type="button" onClick={() => { setSource(url); setResults([]); }} className="ml-auto flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-arrow-go-forward-line"></i>Continuer</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}